import React, { useEffect, useCallback } from 'react';
import { connect } from 'react-redux';
import { Link, useParams } from 'react-router-dom';
import PropTypes from 'prop-types';
import { Container, Row, Col, Card, Alert, Button, Badge } from 'react-bootstrap';
import "bootstrap/dist/css/bootstrap.min.css";
import { getWhatsappTimeline } from '../../actions/whatsappAnalytics';
import { LoadingState, ErrorBanner } from './StateViews';
import { humanize, stageLabel, journeyLabel, offerLabel, formatIstDateTime } from './labels';

const NO_VALUE = '-';
const MESSAGES_PATH = '/inventory/v1.2/whatsapp/messages';
const USERS_PATH = '/inventory/v1.2/users';
const SOURCE_TYPES = ['message', 'event', 'offer', 'support', 'enrollment'];

const rupees = (value) => (value ? `Rs ${Number(value).toLocaleString('en-IN')}` : null);

const joinDetail = (parts) => parts.filter(Boolean).join(' · ');

const messageEntry = (item) => {
    const name = item.stage ? stageLabel(item.stage) : humanize(item.kind);
    const times = [
        item.deliveredAt && `Delivered ${formatIstDateTime(item.deliveredAt)}`,
        item.readAt && `Read ${formatIstDateTime(item.readAt)}`
    ];
    const base = {
        at: item.at,
        detail: joinDetail([item.journey && journeyLabel(item.journey), item.templateName, ...times])
    };
    if (item.status === 'failed') {
        return { ...base, tone: 'danger', title: `${name} message failed`, detail: joinDetail([base.detail, item.failureCode && `Code ${item.failureCode}`]) };
    }
    if (item.status === 'dry_run') {
        return { ...base, tone: 'secondary', title: `${name} message (dry run, not sent)` };
    }
    return { ...base, tone: item.readAt ? 'success' : 'primary', title: `${name} message sent` };
};

const eventEntry = (item) => {
    const where = joinDetail([item.journey && journeyLabel(item.journey), item.stage && stageLabel(item.stage)]);
    const titles = {
        button: `Chose "${item.label || humanize(item.choice || 'option')}"`,
        number: item.label ? `Replied with "${item.label}"` : `Replied with option ${item.choice || NO_VALUE}`,
        stale_tap: 'Tapped an old message',
        optout: 'Opted out of WhatsApp',
        payment: 'Payment update',
        completed: 'Journey completed',
        system: 'System update'
    };
    const tones = { optout: 'warning', completed: 'success', stale_tap: 'secondary', system: 'secondary' };
    return { at: item.at, tone: tones[item.eventType] || 'info', title: titles[item.eventType] || humanize(item.eventType), detail: where };
};

const offerEntries = (item) => {
    const label = offerLabel(item.offerKey);
    const linked = ['link_created', 'paid'].includes(item.status);
    const entries = [{
        at: item.at,
        tone: 'info',
        title: `Offer made: ${label}`,
        detail: joinDetail([rupees(item.price), item.cans && `${item.cans} cans`, linked && 'Payment link created', `Status: ${humanize(item.status)}`])
    }];
    if (item.paidAt) {
        entries.push({ at: item.paidAt, tone: 'success', title: `Offer paid: ${label}`, detail: joinDetail([rupees(item.price)]) });
    }
    return entries;
};

const supportEntries = (item) => {
    const entries = [{
        at: item.at,
        tone: 'warning',
        title: `Support request: ${item.label}`,
        detail: joinDetail([item.categoryLabel, `Status: ${humanize(item.status)}`])
    }];
    if (item.resolvedAt) {
        entries.push({ at: item.resolvedAt, tone: 'success', title: `Support request ${item.status === 'dismissed' ? 'dismissed' : 'resolved'}: ${item.label}`, detail: '' });
    }
    return entries;
};

const enrollmentEntries = (item) => {
    const entries = [{
        at: item.at,
        tone: 'primary',
        title: `Enrolled in ${journeyLabel(item.journey)}`,
        detail: item.currentStage ? `Stage: ${stageLabel(item.currentStage)}` : ''
    }];
    if (item.exitedAt) {
        entries.push({
            at: item.exitedAt,
            tone: 'secondary',
            title: `Left ${journeyLabel(item.journey)}`,
            detail: item.exitReason ? `Reason: ${humanize(item.exitReason)}` : ''
        });
    }
    return entries;
};

const ENTRY_BUILDERS = {
    message: (item) => [messageEntry(item)],
    event: (item) => [eventEntry(item)],
    offer: offerEntries,
    support: supportEntries,
    enrollment: enrollmentEntries
};

// Flattens the API items into dated entries, drops exact repeats and orders them oldest first
export const buildEntries = (items) => {
    const unique = new Map();
    items.forEach((item) => {
        const build = ENTRY_BUILDERS[item.type];
        if (!build || !item.at) return;
        build(item).forEach((entry) => {
            unique.set(`${entry.at}|${entry.title}|${entry.detail}`, entry);
        });
    });
    return [...unique.values()].sort((a, b) => new Date(a.at) - new Date(b.at));
};

const currentEnrollment = (items) => {
    const live = items.filter((item) => item.type === 'enrollment' && ['active', 'paused'].includes(item.status));
    return live.length ? live[live.length - 1] : null;
};

const reachedCap = (items, cap) => SOURCE_TYPES.some((type) => items.filter((item) => item.type === type).length >= cap);

const TimelineHeader = ({ data }) => {
    const enrollment = currentEnrollment(data.items);
    const { user } = data;

    return (
        <Card className="shadow-sm mb-4">
            <Card.Body>
                <Row className="g-3">
                    <Col md={4}>
                        <div className="text-muted small">Customer</div>
                        <div className="fw-semibold fs-5">{user.name || 'Unnamed'}</div>
                        <div className="text-muted">{user.phone}</div>
                    </Col>
                    <Col md={4}>
                        <div className="text-muted small">Current enrollment</div>
                        {enrollment ? (
                            <>
                                <div className="fw-semibold">{journeyLabel(enrollment.journey)}</div>
                                <div>{enrollment.currentStage ? stageLabel(enrollment.currentStage) : NO_VALUE}</div>
                                <div className="text-muted small">
                                    Next action: {enrollment.nextActionAt ? `${formatIstDateTime(enrollment.nextActionAt)} IST` : 'none scheduled'}
                                </div>
                            </>
                        ) : (
                            <div className="text-muted">Not in a journey</div>
                        )}
                    </Col>
                    <Col md={4}>
                        <div className="text-muted small">WhatsApp messages</div>
                        {user.whatsappOptOut ? (
                            <>
                                <Badge bg="danger">Opted out</Badge>
                                {user.whatsappOptOutAt && (
                                    <div className="text-muted small mt-1">Since {formatIstDateTime(user.whatsappOptOutAt)} IST</div>
                                )}
                            </>
                        ) : (
                            <Badge bg="success">Receiving messages</Badge>
                        )}
                    </Col>
                </Row>
            </Card.Body>
        </Card>
    );
};

TimelineHeader.propTypes = {
    data: PropTypes.shape({
        user: PropTypes.object.isRequired,
        items: PropTypes.array.isRequired
    }).isRequired
};

const TimelineList = ({ entries }) => (
    <ol className="list-unstyled mb-0 ms-2 border-start ps-4">
        {entries.map((entry, index) => (
            <li key={`${entry.at}-${index}`} className="position-relative pb-4">
                <span
                    className={`position-absolute rounded-circle bg-${entry.tone}`}
                    style={{ width: 12, height: 12, left: -31, top: 6 }}
                    aria-hidden="true"
                />
                <div className="text-muted small">{formatIstDateTime(entry.at)} IST</div>
                <div className="fw-semibold">{entry.title}</div>
                {entry.detail && <div className="text-muted small">{entry.detail}</div>}
            </li>
        ))}
    </ol>
);

TimelineList.propTypes = {
    entries: PropTypes.arrayOf(PropTypes.shape({
        at: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
        tone: PropTypes.string.isRequired,
        title: PropTypes.string.isRequired,
        detail: PropTypes.string
    })).isRequired
};

const UserTimeline = ({ timeline, error, getWhatsappTimeline }) => {
    const { userId } = useParams();

    const load = useCallback(() => {
        getWhatsappTimeline(userId);
    }, [getWhatsappTimeline, userId]);

    useEffect(() => {
        document.title = 'WhatsApp Timeline';
        load();
    }, [load]);

    const ready = Boolean(timeline && String(timeline.user.id) === userId);

    const renderBody = () => {
        if (!ready) {
            return error ? <ErrorBanner message={error} onRetry={load} /> : <LoadingState />;
        }

        const entries = buildEntries(timeline.items);

        return (
            <>
                <TimelineHeader data={timeline} />
                <Card className="shadow-sm">
                    <Card.Header className="bg-white d-flex justify-content-between align-items-center">
                        <span className="fw-semibold">History, oldest first</span>
                        <span className="text-muted small">{entries.length} entries</span>
                    </Card.Header>
                    <Card.Body>
                        {reachedCap(timeline.items, timeline.capPerSource) && (
                            <Alert variant="warning">
                                Only the newest {timeline.capPerSource} records of each kind are shown, so the earliest history may be missing.
                            </Alert>
                        )}
                        {entries.length === 0 ? (
                            <div className="text-muted">No WhatsApp history for this customer yet.</div>
                        ) : (
                            <TimelineList entries={entries} />
                        )}
                    </Card.Body>
                </Card>
            </>
        );
    };

    return (
        <Container fluid className="py-4">
            <Row className="mb-4 align-items-center g-3">
                <Col xs={12} lg>
                    <h2 className="mb-1">WhatsApp Timeline</h2>
                    <p className="text-muted mb-0">Everything that happened with this customer on WhatsApp. Times are IST.</p>
                </Col>
                <Col xs={12} lg="auto" className="d-flex flex-wrap gap-2">
                    <Button as={Link} to={`${MESSAGES_PATH}?search=${encodeURIComponent(userId)}`} variant="outline-primary" size="sm">
                        View in Message Log
                    </Button>
                    <Button as={Link} to={`${USERS_PATH}/${encodeURIComponent(userId)}`} variant="outline-secondary" size="sm">
                        Open user
                    </Button>
                </Col>
            </Row>
            {renderBody()}
        </Container>
    );
};

UserTimeline.propTypes = {
    timeline: PropTypes.object,
    error: PropTypes.string,
    getWhatsappTimeline: PropTypes.func.isRequired
};

const mapStateToProps = (state) => ({
    timeline: state.whatsappAnalytics.timeline,
    error: state.whatsappAnalytics.error
});

export default connect(mapStateToProps, { getWhatsappTimeline })(UserTimeline);
